import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Box, Container, Typography, AppBar, Toolbar, Button, useTheme, CircularProgress, Divider, Grid, Paper, useMediaQuery } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';

// Import components
import LoadingScreen from './components/LoadingScreen';
import EnhancedBackground from './components/EnhancedBackground';
import IntroAnimation from './components/IntroAnimation';
import RobotPipeline from './components/RobotPipeline';
import ChatWidget from './components/ChatWidget';
import FloatingChatButton from './components/FloatingChatButton';

// Import utilities
import { formatResponse } from './utils/formatting';
import { sanitizeHTML } from './utils/sanitizer';

import './styles/animations.css';

const TestChat = () => {
  const theme = useTheme();
  const { id } = useParams();
  const { token } = useAuth();
  const [bot, setBot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [sending, setSending] = useState(false);
  const chatContainerRef = React.useRef(null);
  const [chatOpen, setChatOpen] = useState(true);
  const [minimized, setMinimized] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showAnimation, setShowAnimation] = useState(true);
  
  // Add responsive breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const fetchBotDetails = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/bots/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setBot(response.data);
        
        // Add welcome message
        setChatHistory([
          { 
            role: 'bot', 
            content: response.data?.customizations?.welcomeMessage || 'Hello! How can I help you today?' 
          }
        ]);
        
        setLoading(false);
        
        // Hide animation after 5 seconds
        setTimeout(() => setShowAnimation(false), 5000);
      } catch (error) {
        console.error('Error fetching bot details:', error);
        setLoading(false);
      }
    };
    
    fetchBotDetails();
  }, [id, token]);

  useEffect(() => {
    // Update scroll behavior to show latest messages at the bottom
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatHistory]);

  const handleSend = async () => {
    if (!message.trim()) return;
    
    // Add user message to chat
    const userMessage = message;
    setChatHistory(prev => [...prev, { role: 'user', content: userMessage }]);
    setMessage('');
    setSending(true);
    
    try {
      // Send message to API
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/chat/${id}`,
        { message: userMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Add bot response
      setChatHistory(prev => [...prev, { 
        role: 'bot', 
        content: response.data.response 
      }]);
    } catch (error) {
      console.error('Error sending message:', error);
      setChatHistory(prev => [...prev, { 
        role: 'bot', 
        content: 'Sorry, I encountered an error processing your message. Please try again later.' 
      }]);
    } finally {
      setSending(false);
    }
  };

  const toggleChat = () => {
    setChatOpen(!chatOpen);
  };

  const toggleMinimize = () => {
    setMinimized(!minimized);
  };

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      display: 'flex', 
      flexDirection: 'column', 
      position: 'relative', 
      overflow: 'hidden', 
      bgcolor: '#121212' 
    }}>
      {/* Enhanced background now covers the entire page with integrated services */}
      <EnhancedBackground bot={bot} />
      
      {/* Intro animation overlay */}
      <IntroAnimation bot={bot} showAnimation={showAnimation} />

      {/* Test Website Header - with higher z-index to appear above the background */}
      <AppBar position="static" sx={{ bgcolor: 'rgba(26, 26, 26, 0.8)', backdropFilter: 'blur(10px)', boxShadow: '0 4px 20px rgba(0,0,0,0.3)', zIndex: 10 }}>
        <Toolbar>
          <Button 
            component={Link}
            to={`/bots/${id}`}
            startIcon={<ArrowBackIcon />}
            sx={{ color: 'white', mr: 2 }}
          >
            Back to Bot
          </Button>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {bot?.name || 'Test Bot'} - Demo Website
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Main Content Area with enhanced welcome section */}
      <Box 
        sx={{ 
          p: { xs: 2, sm: 3, md: 4 }, 
          flexGrow: 1,
          overflowY: 'auto',
          position: 'relative',
          zIndex: 2 // Higher than background but lower than AppBar
        }}
      >
        <Container maxWidth="lg">
          {/* Enhanced Welcome Section */}
          <Paper 
            elevation={3} 
            sx={{
              mt: { xs: 2, sm: 4 },
              mb: { xs: 4, sm: 6 },
              p: { xs: 3, sm: 4 },
              borderRadius: 2,
              background: 'linear-gradient(135deg, rgba(40, 40, 45, 0.9) 0%, rgba(20, 20, 25, 0.85) 100%)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.1)',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* Decorative elements */}
            <Box 
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: { xs: '120px', sm: '180px', md: '220px' },
                height: { xs: '120px', sm: '180px', md: '220px' },
                background: `radial-gradient(circle, ${bot?.customizations?.primaryColor || '#4C85F0'}30, transparent 70%)`,
                opacity: 0.6,
                filter: 'blur(40px)',
                borderRadius: '50%',
                transform: 'translate(30%, -30%)',
                zIndex: 0
              }}
            />
            
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={7}>
                  <Typography 
                    variant={isMobile ? "h4" : "h2"} 
                    component="h1" 
                    gutterBottom 
                    sx={{ 
                      color: 'white',
                      fontWeight: 'bold',
                      animation: 'fadeInUp 0.8s',
                      position: 'relative'
                    }}
                  >
                    Welcome to Our Interactive AI Demo
                    
                    {/* Decorative AI element */}
                    <Box 
                      component="span" 
                      sx={{ 
                        position: 'absolute',
                        top: { xs: '-10px', sm: '-15px' },
                        right: { xs: '-10px', sm: '-15px' },
                        width: { xs: '25px', sm: '40px' },
                        height: { xs: '25px', sm: '40px' },
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='${encodeURIComponent(bot?.customizations?.primaryColor || '#4C85F0')}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M12 2a10 10 0 0 1 10 10a10 10 0 0 1-10 10H2V12A10 10 0 0 1 12 2z'%3E%3C/path%3E%3Cpath d='M8 12a2 2 0 1 1 4 0a2 2 0 0 1-4 0'%3E%3C/path%3E%3Cpath d='M14 12a2 2 0 1 1 4 0a2 2 0 0 1-4 0'%3E%3C/path%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: 'contain',
                        animation: 'float 3s ease-in-out infinite',
                        display: { xs: 'none', md: 'block' }
                      }}
                    />
                  </Typography>
                  
                  <Typography 
                    variant={isMobile ? "body1" : "h5"} 
                    color="rgba(255,255,255,0.8)" 
                    paragraph
                    sx={{ 
                      animation: 'fadeInUp 0.8s 0.2s forwards',
                      opacity: 0,
                      mb: 3
                    }}
                  >
                    This is an interactive environment showcasing {bot?.name || "our AI chatbot"}'s capabilities. 
                    Experience natural conversation with customizable AI features.
                  </Typography>
                  
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      gap: 2, 
                      flexWrap: 'wrap',
                      animation: 'fadeInUp 0.8s 0.4s forwards',
                      opacity: 0 
                    }}
                  >
                    {['Natural Language', 'Custom Responses', 'Real-time Chat', 'API Integration'].map((feature, i) => (
                      <Box 
                        key={feature}
                        sx={{
                          py: 1,
                          px: 2,
                          borderRadius: '20px',
                          bgcolor: 'rgba(255,255,255,0.08)',
                          border: `1px solid ${bot?.customizations?.primaryColor || '#4C85F0'}40`,
                          color: 'white',
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}
                      >
                        <Box 
                          sx={{ 
                            width: '8px', 
                            height: '8px', 
                            borderRadius: '50%', 
                            bgcolor: bot?.customizations?.primaryColor || '#4C85F0',
                            animation: `pulse 1.5s infinite ease-in-out ${i * 0.2}s` 
                          }} 
                        />
                        {feature}
                      </Box>
                    ))}
                  </Box>
                </Grid>
                <Grid item xs={12} md={5} sx={{ display: { xs: 'none', md: 'block' }}}>
                  <Box 
                    sx={{ 
                      width: '100%',
                      height: '220px',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      position: 'relative',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                      animation: 'fadeIn 1s 0.8s forwards',
                      opacity: 0,
                      border: '1px solid rgba(255,255,255,0.1)'
                    }}
                  >
                    {/* Animated AI chat simulation */}
                    <Box 
                      sx={{ 
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(180deg, #1e1e2a 0%, #131318 100%)',
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      <Box sx={{ 
                        p: 1, 
                        borderBottom: '1px solid rgba(255,255,255,0.1)', 
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center' 
                      }}>
                        <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 'medium' }}>
                          {bot?.name || "AI Assistant"} Demo
                        </Typography>
                      </Box>
                      
                      <Box sx={{ 
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        overflow: 'hidden'
                      }}>
                        {/* Bot message */}
                        <Box sx={{ 
                          alignSelf: 'flex-start', 
                          maxWidth: '80%',
                          animation: 'fadeInMessage 0.5s forwards'
                        }}>
                          <Box sx={{ 
                            bgcolor: bot?.customizations?.primaryColor || '#4C85F0',
                            color: 'white',
                            p: 1.5,
                            borderRadius: '12px 12px 12px 0',
                          }}>
                            <Typography variant="body2">
                              Hello! How can I assist you today?
                            </Typography>
                          </Box>
                        </Box>
                        
                        {/* User message */}
                        <Box sx={{ 
                          alignSelf: 'flex-end', 
                          maxWidth: '80%',
                          opacity: 0,
                          animation: 'fadeInMessage 0.5s forwards 1.2s'
                        }}>
                          <Box sx={{ 
                            bgcolor: 'rgba(255,255,255,0.9)',
                            p: 1.5,
                            borderRadius: '12px 12px 0 12px',
                          }}>
                            <Typography variant="body2">
                              What can you tell me about your services?
                            </Typography>
                          </Box>
                        </Box>
                        
                        {/* Typing indicator */}
                        <Box sx={{ 
                          alignSelf: 'flex-start',
                          opacity: 0,
                          animation: 'fadeInMessage 0.5s forwards 2.4s'
                        }}>
                          <Box sx={{ 
                            bgcolor: bot?.customizations?.primaryColor || '#4C85F0',
                            p: 1.2,
                            borderRadius: '12px',
                            display: 'flex',
                            gap: 0.5
                          }}>
                            <Box className="typing-dot" />
                            <Box className="typing-dot" />
                            <Box className="typing-dot" />
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
          
          {/* Robot Pipeline Section - now with better responsive placement and increased spacing */}
          <Box sx={{ 
            mb: { xs: 5, sm: 7 }, // Increased bottom margin
            mt: { xs: 1, sm: 2 }, // Added top margin
            position: 'relative',
            zIndex: 2
          }}>
            <Typography 
              variant={isMobile ? "h5" : "h4"} 
              component="h2" 
              gutterBottom 
              sx={{ 
                color: 'white',
                textAlign: 'center',
                fontWeight: 'medium',
                position: 'relative',
                mb: 3,
                '&:after': {
                  content: '""',
                  position: 'absolute',
                  bottom: '-8px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '60px',
                  height: '3px',
                  backgroundColor: bot?.customizations?.primaryColor || '#4C85F0'
                }
              }}
            >
              How Our AI Works
            </Typography>
            
            <RobotPipeline bot={bot} />
            
            <Divider sx={{ my: { xs: 4, sm: 5 }, bgcolor: 'rgba(255,255,255,0.1)' }} />
          </Box>
          
          {/* Services and Interaction Section - rearranged for better layout */}
          <Grid container spacing={isTablet ? 4 : 6} sx={{ mb: 6 }}>
            {/* First column: AI Services - implemented directly in TestChat */}
            <Grid item xs={12} md={6}>
              <Paper elevation={3} sx={{
                borderRadius: 2,
                height: '100%',
                p: 3,
                background: 'linear-gradient(135deg, rgba(40, 40, 45, 0.7) 0%, rgba(20, 20, 25, 0.7) 100%)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.1)',
                position: 'relative'
              }}>
                <Box sx={{
                  position: 'absolute',
                  top: '-20px',
                  left: '20px',
                  backgroundColor: bot?.customizations?.primaryColor || theme.palette.primary.main,
                  color: 'white',
                  borderRadius: '20px',
                  py: 1,
                  px: 2,
                  fontWeight: 'bold',
                  boxShadow: '0 5px 15px rgba(0,0,0,0.2)',
                  zIndex: 2
                }}>
                  Our AI Services
                </Box>
                
                <Box sx={{ pt: 2 }}>
                  {/* Service Items */}
                  <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    {[
                      { name: 'Chatbot Development', icon: 'message-circle', delay: 0, color: '#4C85F0' },
                      { name: 'NLP Solutions', icon: 'brain', delay: 0.2, color: '#9C27B0' },
                      { name: 'Data Analysis', icon: 'trending-up', delay: 0.4, color: '#FF9800' },
                      { name: 'AI Training', icon: 'code', delay: 0.6, color: '#4CAF50' },
                    ].map((service, index) => (
                      <Box
                        key={service.name}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          opacity: 0,
                          animation: `fadeInLeft 0.8s ease-out forwards ${service.delay}s`
                        }}
                      >
                        <Box
                          sx={{
                            width: { xs: 40, sm: 50 },
                            height: { xs: 40, sm: 50 },
                            borderRadius: '12px',
                            backgroundColor: 'rgba(255,255,255,0.05)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 2,
                            position: 'relative',
                            border: `1px solid rgba(255,255,255,0.1)`,
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              width: '100%',
                              height: '3px',
                              bottom: 0,
                              left: 0,
                              backgroundColor: service.color,
                            }
                          }}
                        >
                          {/* Fixed icon rendering with SVG component */}
                          <svg 
                            width="24" 
                            height="24" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke={service.color} 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                            style={{
                              animation: `pulse 3s infinite ease-in-out ${index * 0.5}s`
                            }}
                          >
                            <path d={getIconPath(service.icon)}></path>
                          </svg>
                        </Box>
                        <Box>
                          <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 'medium' }}>
                            {service.name}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                            {getServiceDescription(service.name)}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Paper>
            </Grid>
            
            {/* Second column: How to Interact - implemented directly in TestChat */}
            <Grid item xs={12} md={6}>
              <Paper elevation={3} sx={{
                borderRadius: 2,
                height: '100%',
                p: 3,
                background: 'linear-gradient(135deg, rgba(40, 40, 45, 0.7) 0%, rgba(20, 20, 25, 0.7) 100%)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.1)',
                position: 'relative'
              }}>
                <Box sx={{
                  position: 'absolute',
                  top: '-20px',
                  left: '20px',
                  backgroundColor: bot?.customizations?.primaryColor || theme.palette.primary.main,
                  color: 'white',
                  borderRadius: '20px',
                  py: 1,
                  px: 2,
                  fontWeight: 'bold',
                  boxShadow: '0 5px 15px rgba(0,0,0,0.2)',
                  zIndex: 2
                }}>
                  How to Interact
                </Box>
                
                <Box sx={{ pt: 2 }}>
                  {/* Chat demo */}
                  <Box
                    sx={{
                      mb: 3,
                      p: 2,
                      backgroundColor: 'rgba(30,30,35,0.5)',
                      borderRadius: 2,
                      position: 'relative',
                      border: '1px solid rgba(255,255,255,0.05)',
                      overflow: 'hidden'
                    }}
                  >
                    {/* Bot message */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        opacity: 0,
                        animation: 'fadeInMessage 0.5s forwards'
                      }}
                    >
                      <Box
                        sx={{
                          p: 1.5,
                          bgcolor: bot?.customizations?.primaryColor || theme.palette.primary.main,
                          color: 'white',
                          borderRadius: '12px 12px 12px 0',
                          maxWidth: '80%',
                        }}
                      >
                        <Typography variant="body2">
                          Hello! How can I assist you today?
                        </Typography>
                      </Box>
                    </Box>
                    
                    {/* User message - appears after delay */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'flex-end',
                        mt: 2,
                        opacity: 0,
                        animation: 'fadeInMessage 0.5s forwards 0.7s'
                      }}
                    >
                      <Box
                        sx={{
                          p: 1.5,
                          bgcolor: 'rgba(255,255,255,0.9)',
                          borderRadius: '12px 12px 0 12px',
                          maxWidth: '80%',
                        }}
                      >
                        <Typography variant="body2">
                          What services do you offer?
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  
                  {/* Step-by-step guide */}
                  <Typography paragraph sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Use the chat widget to interact with our AI assistant. Get answers to your questions instantly.
                  </Typography>
                  
                  {/* Steps */}
                  <Box sx={{ mt: 2 }}>
                    {[
                      { text: 'Click the chat button in the corner', icon: 'message-circle', delay: 0.2 },
                      { text: 'Type your question or request', icon: 'edit', delay: 0.4 },
                      { text: 'Get instant AI-powered responses', icon: 'zap', delay: 0.6 },
                    ].map((step, index) => (
                      <Box 
                        key={step.text}
                        sx={{ 
                          display: 'flex',
                          alignItems: 'center',
                          mb: 1.5,
                          opacity: 0,
                          animation: `fadeInRight 0.5s forwards ${step.delay}s`,
                          p: 1.5,
                          borderRadius: 1,
                          bgcolor: 'rgba(255,255,255,0.05)',
                          transition: 'transform 0.3s ease, background-color 0.3s ease',
                          '&:hover': {
                            transform: 'translateX(5px)',
                            bgcolor: 'rgba(255,255,255,0.1)',
                          }
                        }}
                      >
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 2,
                            color: 'white',
                            fontWeight: 'bold',
                            border: `2px solid ${bot?.customizations?.primaryColor || theme.palette.primary.main}`,
                            bgcolor: 'rgba(255,255,255,0.1)',
                          }}
                        >
                          {index + 1}
                        </Box>
                        <Typography variant="body2" sx={{ color: 'white', flex: 1 }}>
                          {step.text}
                        </Typography>
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='${encodeURIComponent(bot?.customizations?.primaryColor || theme.palette.primary.main)}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E${getIconPath(step.icon)}%3C/svg%3E")`,
                            backgroundRepeat: 'no-repeat',
                            backgroundSize: 'contain',
                            filter: index === 2 ? 'drop-shadow(0 0 3px rgba(76, 133, 240, 0.6))' : 'none',
                            animation: index === 2 ? 'glowIcon 1.5s infinite alternate' : 'none'
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
          
          {/* Bottom spacing for mobile view so the chat widget doesn't overlap content */}
          <Box sx={{ height: { xs: '120px', sm: '150px' } }} />
        </Container>
      </Box>

      {/* Chat Widget Button - only shown when chat is closed */}
      <FloatingChatButton bot={bot} chatOpen={chatOpen} toggleChat={toggleChat} />

      {/* Floating Chat Widget - KEEPING UNCHANGED as requested */}
      <ChatWidget 
        bot={bot}
        chatHistory={chatHistory}
        message={message}
        setMessage={setMessage}
        sending={sending}
        handleSend={handleSend}
        formatResponse={formatResponse}
        chatContainerRef={chatContainerRef}
        minimized={minimized}
        toggleMinimize={toggleMinimize}
        expanded={expanded}
        toggleExpanded={toggleExpanded}
        toggleChat={toggleChat}
        floating={true}
        chatOpen={chatOpen}
      />
    </Box>
  );
};

// Helper function to get icon path for SVG icons
const getIconPath = (icon) => {
  switch(icon) {
    case 'message-circle': 
      return 'M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z';
    case 'brain': 
      return 'M9.5 2h5M12 2v8m3.5-6h-8m10 6v4m-12 0v4m0-9v1m12-1v1m0 8a2 2 0 1 0 0-4m-12 4a2 2 0 1 1 0-4';
    case 'trending-up': 
      return 'M23 6l-9.5 9.5-5-5L1 18m16-5l6-6m-6 0h6v6';
    case 'edit': 
      return 'M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7m-9 1V9.5a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1V16a1 1 0 0 1-1 1h-6a1 1 0 0 1-1-1z';
    case 'zap': 
      return 'M13 2L3 14h9l-1 8 10-12h-9l1-8z';
    case 'code': 
      return 'M16 18l6-6-6-6M8 6l-6 6 6 6';
    default: 
      return '';
  }
};

// Helper function to get service descriptions
const getServiceDescription = (serviceName) => {
  switch(serviceName) {
    case 'Chatbot Development':
      return 'Custom AI assistants for your business needs';
    case 'NLP Solutions':
      return 'Natural language processing capabilities';
    case 'Data Analysis':
      return 'Insights extraction from your business data';
    case 'AI Training':
      return 'Custom training for your specific industry';
    default:
      return '';
  }
};

export default TestChat;
